import { matchPath } from 'react-router-dom';
import { ROUTES, INTERNAL_ROUTING } from 'shared/constants';

/**
 * Gets the most specific resource ID from route and query parameters
 * Prioritizes any query parameter value over route parameter IDs
 * @param {string} pathname - The current pathname
 * @param {string} search - The current search string (query parameters)
 * @returns {string|null} - The most specific resource ID or null if none found
 */
export const getMostSpecificResourceId = (pathname, search) => {
  // Parse query parameters
  const searchParams = new URLSearchParams(search);

  // If there are any query parameters, return the first one's value
  // This automatically scales with any new query parameters added
  for (const [, value] of searchParams.entries()) {
    if (value) {
      return value;
    }
  }

  // If no query parameters, extract route parameter ID
  let routeParams = {};
  Object.values(ROUTES).find(route => {
    const matchResult = matchPath(route, pathname);
    if (matchResult?.params) {
      routeParams = matchResult.params;
      return true;
    }
    return false;
  });

  // Return route parameter ID if available
  if (routeParams.id) {
    return routeParams.id;
  }

  return null;
};

/**
 * Gets the analytics payload for the current route
 * @param {string} pathname - The current pathname
 * @param {string} search - The current search string (query parameters)
 * @returns {object} - Analytics payload object
 */
export const getAnalyticsPayload = (pathname, search) => {
  // Parse query parameters
  const searchParams = new URLSearchParams(search);
  const queryParams = {};
  for (const [key, value] of searchParams.entries()) {
    queryParams[key] = value;
  }

  // Get the route mappings
  const routeTitleMappings = getRouteMappings();

  for (const { route, condition, analyticsPayload } of routeTitleMappings) {
    const routes = Array.isArray(route) ? route : [route];

    for (const r of routes) {
      const matchResult = matchPath(r, pathname);
      if (!matchResult) continue;

      const params = matchResult.params || {};
      const conditionPassed = condition ? condition(params, queryParams) : true;

      if (conditionPassed) {
        return analyticsPayload ? analyticsPayload(params, queryParams) : {};
      }
    }
  }

  return {};
};

/**
 * Gets the route mappings configuration
 * @returns {Array} - Array of route mapping objects
 */
const getRouteMappings = () => [
  // Knowledge routes with specific query parameter handling
  {
    route: ROUTES.KNOWLEDGE, // '/knowledge'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE], // has 'category' query param
    title: 'Knowledge Binder',
    analyticsPayload: (params, query) => ({
      id: query[INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE],
    }),
  },
  {
    route: ROUTES.KNOWLEDGE_CATEGORIES, // '/knowledge/:id'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE], // has 'category' query param
    title: 'Knowledge Binder',
    analyticsPayload: (params, query) => ({
      id: query[INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE],
    }),
  },
  {
    route: ROUTES.KNOWLEDGE_CATEGORIES, // '/knowledge/:id'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE], // no 'category' query param
    title: 'Knowledge Category',
    analyticsPayload: (params, query) => ({
      id: params.id,
    }),
  },
  {
    route: ROUTES.KNOWLEDGE_ITEMS, // '/knowledge/items/:id'
    title: 'Knowledge Item',
    analyticsPayload: (params, query) => ({
      itemId: params.id,
    }),
  },
  {
    route: ROUTES.KNOWLEDGE_ITEMS_NEW, // '/knowledge/items/new'
    title: 'Knowledge Item (New)',
  },
  {
    route: ROUTES.KNOWLEDGE_ITEMS_EDIT, // '/knowledge/items/:id/edit'
    title: 'Knowledge Item (Edit)',
    analyticsPayload: (params, query) => ({
      itemId: params.id,
    }),
  },
  {
    route: ROUTES.KNOWLEDGE, // '/knowledge'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE], // no 'category' query param
    title: 'Knowledge Base',
  },

  // Lab/Compendium routes
  {
    route: ROUTES.LABS, // '/compendium'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM], // has 'values' query param
    title: 'Compendium Binder',
    analyticsPayload: (params, query) => ({
      id: query[INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM],
    }),
  },
  {
    route: ROUTES.LABS_CATEGORIES, // '/compendium/:id'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM], // has 'values' query param
    title: 'Compendium Binder',
    analyticsPayload: (params, query) => ({
      id: query[INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM],
    }),
  },
  {
    route: ROUTES.LABS_CATEGORIES, // '/compendium/:id'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM], // no 'values' query param
    title: 'Compendium Category',
    analyticsPayload: (params, query) => ({
      id: params.id,
    }),
  },
  {
    route: ROUTES.LABS, // '/compendium'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM], // no 'values' query param
    title: 'Compendium',
  },

  // Admission routes
  {
    route: ROUTES.ADMISSION, // '/admission'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST], // has 'checklist' query param
    title: 'Checklist Binder',
    analyticsPayload: (params, query) => ({
      checklistId: query[INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST],
    }),
  },
  {
    route: ROUTES.ADMISSION_CATEGORIES, // '/admission/:id'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST], // has 'checklist' query param
    title: 'Checklist Binder',
    analyticsPayload: (params, query) => ({
      checklistId: query[INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST],
    }),
  },
  {
    route: ROUTES.ADMISSION_CATEGORIES, // '/admission/:id'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST], // no 'checklist' query param
    title: 'Checklist Category',
    analyticsPayload: (params, query) => ({
      id: params.id,
    }),
  },
  {
    route: ROUTES.ADMISSION, // '/admission'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST], // no 'checklist' query param
    title: 'Checklists',
  },

  // Knowledge Hub routes
  {
    route: ROUTES.KNOWLEDGE_HUB, // '/hub'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.HUB], // has 'hub-category' query param
    title: 'Knowledge Hub Binder',
    analyticsPayload: (params, query) => ({
      id: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB],
      organizationId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_ORGANIZATION],
      workspaceId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_WORKSPACE],
      categoryId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_CATEGORY],
    }),
  },
  {
    route: ROUTES.KNOWLEDGE_HUB_ITEMS, // '/hub/items/:id'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.HUB], // has 'hub-category' query param
    title: 'Knowledge Hub Binder',
    analyticsPayload: (params, query) => ({
      id: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB],
      organizationId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_ORGANIZATION],
      workspaceId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_WORKSPACE],
      categoryId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_CATEGORY],
    }),
  },
  {
    route: ROUTES.KNOWLEDGE_HUB_ITEMS, // '/hub/items/:id'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.HUB], // no 'hub-category' query param
    title: 'Knowledge Hub Item',
    analyticsPayload: (params, query) => ({
      id: params.id,
      organizationId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_ORGANIZATION],
      workspaceId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_WORKSPACE],
      categoryId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_CATEGORY],
    }),
  },
  {
    route: ROUTES.KNOWLEDGE_HUB_SEARCH, // '/hub/search'
    title: 'Knowledge Hub Search',
    analyticsPayload: (params, query) => ({
      organizationId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_ORGANIZATION],
      workspaceId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_WORKSPACE],
      categoryId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_CATEGORY],
      searchQuery: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_SEARCH],
    }),
  },
  {
    route: ROUTES.KNOWLEDGE_HUB, // '/hub'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.HUB], // no 'hub-category' query param
    title: 'Knowledge Hub',
    analyticsPayload: (params, query) => ({
      organizationId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_ORGANIZATION],
      workspaceId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_WORKSPACE],
      categoryId: query[INTERNAL_ROUTING.QUERY_PARAMS.HUB_CATEGORY],
    }),
  },

  // Contact routes
  {
    route: ROUTES.CONTACTS, // '/contacts'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.CONTACTS], // has 'id' query param
    title: 'Contact Binder',
    analyticsPayload: (params, query) => ({
      id: query[INTERNAL_ROUTING.QUERY_PARAMS.CONTACTS],
    }),
  },
  {
    route: ROUTES.CONTACTS_EDIT, // '/contacts/:id/edit'
    title: 'Edit Contact',
    analyticsPayload: (params, query) => ({
      id: params.id,
    }),
  },
  {
    route: ROUTES.CONTACTS_PREVIEW, // '/contacts/:id'
    title: 'Contact Details',
    analyticsPayload: (params, query) => ({
      id: params.id,
    }),
  },
  {
    route: ROUTES.CONTACTS, // '/contacts'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.CONTACTS], // no 'id' query param
    title: 'Contacts',
  },

  // Communication routes
  {
    route: ROUTES.COMMUNICATION_ITEM, // '/communication/:id'
    title: 'Communication Item',
    analyticsPayload: (params, query) => ({
      id: params.id,
    }),
  },
  {
    route: ROUTES.COMMUNICATION_CENTER, // '/communication'
    title: 'Communication Center',
  },

  // Settings routes
  {
    route: ROUTES.SETTINGS_WORKSPACES, // '/settings/workspaces'
    title: 'Workspace Settings',
  },
  {
    route: ROUTES.SETTINGS_MAGIC_LINK, // '/settings/magic-link'
    title: 'Magic Link Settings',
  },
  {
    route: ROUTES.SETTINGS, // '/settings'
    title: 'Settings',
  },

  // User management routes
  {
    route: ROUTES.USERS_MANAGEMENT, // '/users'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.USERS], // has 'id' query param
    title: 'User Binder',
    analyticsPayload: (params, query) => ({
      id: query[INTERNAL_ROUTING.QUERY_PARAMS.USERS],
    }),
  },
  {
    route: ROUTES.USERS_MANAGEMENT, // '/users'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.USERS], // no 'id' query param
    title: 'User Management',
  },
  {
    route: ROUTES.OWNERS_MANAGEMENT, // '/owners'
    title: 'Owners Management',
  },
  {
    route: ROUTES.USER_PROFILE, // '/profile'
    title: 'User Profile',
  },

  // Other routes
  {
    route: ROUTES.ROTATIONS, // '/rotations'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS], // has 'id' query param
    title: 'Rotation Item',
    analyticsPayload: (params, query) => ({
      id: query[INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS],
      filter: query[INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS_FILTER],
      tab: query[INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS_TAB],
    }),
  },
  {
    route: ROUTES.ROTATIONS, // '/rotations'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS], // no 'id' query param
    title: 'Rotations',
    analyticsPayload: (params, query) => ({
      filter: query[INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS_FILTER],
      tab: query[INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS_TAB],
    }),
  },
  {
    route: ROUTES.SEARCH, // '/search'
    title: 'Search',
  },
  {
    route: ROUTES.FAVORITES, // '/favorites'
    title: 'Favorites',
  },
  {
    route: ROUTES.SUGGEST_CONTENT, // '/suggest-content'
    title: 'Suggest Content',
  },
  {
    route: ROUTES.QUALITY_METRIC, // '/quality-metric'
    title: 'Quality Metrics',
  },
  {
    route: ROUTES.RESTRICTED, // '/restricted'
    condition: (params, query) => query[INTERNAL_ROUTING.QUERY_PARAMS.RESTRICTED_ID], // has 'id' query param
    title: 'Access Restricted',
    analyticsPayload: (params, query) => ({
      id: query[INTERNAL_ROUTING.QUERY_PARAMS.RESTRICTED_ID],
      entity: query[INTERNAL_ROUTING.QUERY_PARAMS.RESTRICTED_ENTITY],
    }),
  },
  {
    route: ROUTES.RESTRICTED, // '/restricted'
    condition: (params, query) => !query[INTERNAL_ROUTING.QUERY_PARAMS.RESTRICTED_ID], // no 'id' query param
    title: 'Access Restricted',
  },
  {
    route: [ROUTES.ROOT, ROUTES.HOME], // '/'
    title: 'Dashboard',
  },

  // Authentication routes
  {
    route: ROUTES.WELCOME, // '/signin'
    title: 'Sign In',
  },
  {
    route: ROUTES.OTP_VERIFICATION, // '/verification'
    title: 'Verification',
  },
  {
    route: ROUTES.NOT_ALLOWED, // '/not-allowed'
    title: 'Not Allowed',
  },
  {
    route: ROUTES.OAUTH_CALLBACK, // '/auth/callback/:provider'
    title: 'Authentication Callback',
  },
];

/**
 * Maps route paths and query parameters to human-readable page titles
 * @param {string} pathname - The current pathname
 * @param {string} search - The current search string (query parameters)
 * @returns {string} - Human-readable page title
 */
export const getPageTitle = (pathname, search) => {
  // Parse query parameters
  const searchParams = new URLSearchParams(search);
  const queryParams = {};
  for (const [key, value] of searchParams.entries()) {
    queryParams[key] = value;
  }

  // Get the route mappings
  const routeTitleMappings = getRouteMappings();

  for (const { route, condition, title } of routeTitleMappings) {
    const routes = Array.isArray(route) ? route : [route];

    for (const r of routes) {
      const matchResult = matchPath(r, pathname);
      if (!matchResult) continue;

      const params = matchResult.params || {};
      const conditionPassed = condition ? condition(params, queryParams) : true;

      if (conditionPassed) {
        return title;
      }
    }
  }

  // Fallback: return a cleaned version of the pathname
  return pathname
    .split('/')
    .filter(segment => segment && !segment.match(/^[0-9a-f-]{36}$/i)) // Remove UUIDs
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

/**
 * Helper function to get a human-readable page title from location object
 * @param {object} location - React Router location object
 * @returns {string} - Human-readable page title
 */
export const getPageTitleFromLocation = location => {
  return getPageTitle(location.pathname, location.search);
};
